/**
 * Sentry error-tracking bootstrap for LexIQ frontend.
 *
 * Init runs once from `src/index.js` BEFORE `ReactDOM.render`. It is a
 * silent no-op when:
 *   • `REACT_APP_SENTRY_DSN` is empty (default in dev/preview)
 *   • `NODE_ENV === "development"` (running `yarn start` locally)
 *
 * What we send:
 *   • Uncaught JS exceptions
 *   • React render errors caught by <ErrorBoundary />
 *   • Explicit `Sentry.captureException(...)` calls
 *
 * What we DO NOT send (privacy + Free-tier budget):
 *   • Session replay video (bandwidth + PII risk)
 *   • Performance traces (`tracesSampleRate: 0.0`)
 *   • Cookies, Authorization headers, query params with token/otp/password/code
 *   • The default Sentry user PII fields (IP, email)
 *
 * Iraqi lawyer data — chat messages, cassation decisions, client names —
 * MUST never appear in stack traces. The `beforeSend` hook is our
 * fail-closed guard.
 */
import * as Sentry from "@sentry/react";

const DSN = (process.env.REACT_APP_SENTRY_DSN || "").trim();
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV || "preview";
const RELEASE = process.env.REACT_APP_BUILD_VERSION || "dev";
const ENABLED = Boolean(DSN) && process.env.NODE_ENV !== "development";

const QUERY_KEY_RE = /(token|code|otp|password|jwt|secret)/i;
const DROP_HEADER_LC = new Set(["authorization", "cookie", "set-cookie", "x-api-key"]);
const JWT_RE = /\beyJ[0-9A-Za-z_-]+\.[0-9A-Za-z_-]+\.[0-9A-Za-z_-]+\b/;
const BCRYPT_RE = /^\$2[abyx]?\$\d{2}\$[./A-Za-z0-9]{53}$/;

function redactUrl(raw) {
  if (!raw) return raw;
  try {
    const u = new URL(raw, window.location.origin);
    for (const key of Array.from(u.searchParams.keys())) {
      if (QUERY_KEY_RE.test(key)) u.searchParams.set(key, "[REDACTED]");
    }
    return u.toString();
  } catch {
    return raw;
  }
}

function scrubValue(v) {
  if (typeof v === "string") {
    if (JWT_RE.test(v)) return "[REDACTED_JWT]";
    if (BCRYPT_RE.test(v)) return "[REDACTED_BCRYPT]";
  }
  return v;
}

function scrubDeep(value) {
  if (Array.isArray(value)) return value.map(scrubDeep);
  if (value && typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      const lk = k.toLowerCase();
      if (DROP_HEADER_LC.has(lk)) continue;
      if (lk === "cookies" || lk === "cookie") continue;
      if (["password", "password_hash", "otp", "authorization", "bar_barcode", "date_of_birth"].includes(lk)) {
        out[k] = "[REDACTED]";
      } else if (lk === "url" || lk === "query_string") {
        out[k] = redactUrl(String(v || ""));
      } else {
        out[k] = scrubDeep(v);
      }
    }
    return out;
  }
  return scrubValue(value);
}

function beforeSend(event) {
  try {
    const e = scrubDeep(event);
    if (e.request) {
      if (e.request.headers) e.request.headers = scrubDeep(e.request.headers);
      delete e.request.cookies;
      if (e.request.url) e.request.url = redactUrl(e.request.url);
      // Drop the entire request body for auth endpoints — they carry
      // plaintext passwords and OTPs that must never leave the browser.
      const path = (() => {
        try {
          return new URL(e.request.url || "", window.location.origin).pathname;
        } catch {
          return "";
        }
      })();
      if (path.startsWith("/api/auth/")) {
        delete e.request.data;
        delete e.request.body;
      }
    }
    if (e.user) {
      delete e.user.ip_address;
      delete e.user.email;
      delete e.user.username;
    }
    return e;
  } catch (err) {
    // Fail closed — if our scrubber breaks, drop the event rather than
    // risk leaking unredacted PII to Sentry.
    return null;
  }
}

export function initSentry() {
  if (!ENABLED) return false;
  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      release: RELEASE,
      sendDefaultPii: false,
      tracesSampleRate: 0.0,
      // NOTE: Do NOT include Sentry.replayIntegration() — session replay is
      // both privacy-hostile (records typed passwords) and Free-tier-heavy.
      integrations: [Sentry.browserTracingIntegration()],
      beforeSend,
      // Prevent Sentry from auto-injecting the current URL as a breadcrumb
      // with query strings that may contain OTP tokens.
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb.data && breadcrumb.data.url) {
          breadcrumb.data.url = redactUrl(String(breadcrumb.data.url));
        }
        return breadcrumb;
      },
    });
    return true;
  } catch (err) {
    // Never crash the app on Sentry init failure — the user should still
    // be able to use LexIQ if error tracking is misconfigured.
    // eslint-disable-next-line no-console
    console.warn("Sentry init failed:", err);
    return false;
  }
}

// Re-export for use in <ErrorBoundary> wrappers elsewhere.
export { Sentry };
